import Fetcher from 'services/Fetcher'
import { API_DICTIONARY, HTTP_METHODS } from 'constants/api'
import {
  ContractDetailsResponse,
  GetVendorContractStewardsArgs,
  IContractDetails,
  ManualContractCreationRequestData,
  SetupContractDetailsTimeRequestData,
  TAvailableOwnersParams,
  TContractAcceptPayload,
  TContractAttchmentUploadFile,
  TContractDocument,
  TContractDocumentTempStore,
  TContractUploadFile,
  TContractUploadTempFile,
  TCSResponse,
  TEvaluationDocuments,
  TEvaluationDocumentsUploadResponse,
  TNotesResponse,
  TNotesUpdateRequest,
  TOwnerData
} from 'features/ContractDetails/types'
import { TResponsibleUser, TRFPDocument } from 'features/RFP/RFPDetails/types'
import { RebateDataRequest } from 'features/ContractDetails/RebateConditions/RebateConditionsForm/types'
import { TUploadPurchaseOrders } from '../PurchaseOrders/types'
import { THSAmendmentFile, TLoPDocument } from './Facilities/types'
import { TUploadedPoDocumentsResponse } from '../CognusRepository/OpportunityAnalysis/SupportInformation/types'
import {
  TClinicalReview,
  TClinicalReviewHospitalUserItem
} from '../CognusRepository/OpportunityAnalysis/types'
import {
  ResponseWithPagination,
  TOptionsResponse,
  TOptionsWithNameResponse
} from '../../constants'
import { TContractUsersData, TContractUsersRequest } from './Users/types'
import {
  TAddSignatoryRequestData,
  TReassignSignatoryAction,
  TSignatoryUserOption
} from './ContractDetailsGeneral/ContractDetailsDocuments/AddSignatoryPopup/types'

const fetcher = new Fetcher({})

export const getContractDetailsRequestAsync = async (id: string) =>
  fetcher.request<unknown, ContractDetailsResponse>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/`,
    method: HTTP_METHODS.GET
  })

export const createContractRequestAsync = async (
  data: Partial<ManualContractCreationRequestData>
) =>
  fetcher.request<Partial<ManualContractCreationRequestData>, IContractDetails>(
    {
      url: `${API_DICTIONARY.CONTRACTS}${API_DICTIONARY.MANUAL_CREATION}`,
      method: HTTP_METHODS.POST,
      data
    }
  )

export const updateContractDetailsRequestAsync = async (
  id: string,
  data: Partial<IContractDetails>
) =>
  fetcher.request<Partial<IContractDetails>, IContractDetails>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/`,
    method: HTTP_METHODS.PATCH,
    data
  })

export const setupContractDetailsTimeRequestAsync = async (
  id: string,
  timelines: SetupContractDetailsTimeRequestData
) =>
  fetcher.request<
    { timelines: SetupContractDetailsTimeRequestData },
    undefined
  >({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.TIMELINES}`,
    method: HTTP_METHODS.POST,
    data: { timelines }
  })

export const getContractDocumentsRequestAsync = async (id: string) =>
  fetcher.request<unknown, ResponseWithPagination<TContractDocument>>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.DOCUMENTS}`,
    method: HTTP_METHODS.GET
  })

export const uploadContractDocumentToTempStore = async (
  file: FormDataEntryValue | null
) =>
  fetcher.request<
    { file: FormDataEntryValue | null },
    TContractDocumentTempStore
  >({
    url: `${API_DICTIONARY.FILES}${API_DICTIONARY.CONTRACT_DOCUMENTS}`,
    method: HTTP_METHODS.POST,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    data: {
      file
    }
  })

export const uploadNewContractDocumentsRequestAsync = async (
  data: TContractUploadFile
) =>
  fetcher.request<unknown, unknown>({
    url: `${API_DICTIONARY.CONTRACTS}${data.contract}/${API_DICTIONARY.DOCUMENTS}`,
    method: HTTP_METHODS.POST,
    data: [...data.files]
  })

export const uploadUpdatedContractDocumentsRequestAsync = async (
  data: TContractUploadFile,
  documentId: string
) =>
  fetcher.request<Pick<TContractUploadTempFile, 'uploaded_file'>, unknown>({
    url: `${API_DICTIONARY.CONTRACTS}${data.contract}/${API_DICTIONARY.DOCUMENTS}${documentId}/`,
    method: HTTP_METHODS.PUT,
    data: {
      uploaded_file: data.files[0].uploaded_file
    }
  })

export const deleteContractDocumentRequestAsync = async (
  data: TContractAcceptPayload
) =>
  fetcher.request<unknown, unknown>({
    url: `${API_DICTIONARY.CONTRACTS}${data.contractId}/${API_DICTIONARY.DOCUMENTS}${data.documentId}/`,
    method: HTTP_METHODS.DELETE
  })

export const acceptContractDocumentRequestAsync = async (
  data: TContractAcceptPayload
) =>
  fetcher.request<unknown, unknown>({
    url: `${API_DICTIONARY.CONTRACTS}${data.contractId}/${API_DICTIONARY.CONTRACT_DOCUMENT}${data.documentId}/${API_DICTIONARY.APPROVE}`,
    method: HTTP_METHODS.POST
  })

export const uploadContractEvaluationDocumentsRequestAsync = async (
  data: TUploadPurchaseOrders
) =>
  fetcher.request<TUploadPurchaseOrders, THSAmendmentFile>({
    url: `${API_DICTIONARY.FILES}${API_DICTIONARY.CONTRACT_EVALUATION_DOCUMENTS}`,
    method: HTTP_METHODS.POST,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    data
  })

export const uploadContractAttachmentsRequestAsync = async (
  data: TContractAttchmentUploadFile
) =>
  fetcher.request<TContractAttchmentUploadFile, TRFPDocument>({
    url: `${API_DICTIONARY.FILES}${API_DICTIONARY.CONTRACT_ATTACHMENTS}`,
    method: HTTP_METHODS.POST,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    data
  })

export const extendContractDetailsRequestAsync = async (data: FormData) =>
  fetcher.request<FormData, unknown>({
    url: `${API_DICTIONARY.FILES}${API_DICTIONARY.CONTRACT_EXTENSION_DOCUMENTS}`,
    method: HTTP_METHODS.POST,
    data
  })

export const updateContractRebateRequestAsync = async (
  id: string,
  data: RebateDataRequest
) =>
  fetcher.request<RebateDataRequest, unknown>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.REBATE}`,
    method: HTTP_METHODS.POST,
    data
  })

export const updateExistingContractRebateRequestAsync = async (
  contractId: string,
  rebateId: string,
  data: RebateDataRequest
) =>
  fetcher.request<RebateDataRequest, unknown>({
    url: `${API_DICTIONARY.CONTRACTS}${contractId}/${API_DICTIONARY.REBATE}${rebateId}/`,
    method: HTTP_METHODS.PUT,
    data
  })

export const getVendorsRequestAsync = async () =>
  fetcher.request<undefined, ResponseWithPagination<TOptionsWithNameResponse>>({
    url: `${API_DICTIONARY.VENDORS_SHORT}`,
    method: HTTP_METHODS.GET
  })

export const getRebateTypesRequestAsync = async () =>
  fetcher.request<undefined, ResponseWithPagination<TOptionsResponse>>({
    url: `${API_DICTIONARY.CONTRACTS}${API_DICTIONARY.OPTIONS}${API_DICTIONARY.REBATE_TYPE}`,
    method: HTTP_METHODS.GET
  })

export const getVendorContractStewardsRequestAsync = async ({
  vendors,
  contract_categories
}: GetVendorContractStewardsArgs) =>
  fetcher.request<undefined, TCSResponse>({
    url: `${API_DICTIONARY.USERS}${
      API_DICTIONARY.VENDOR_CONTRACT_STEWARDS
    }?limit=0&offset=0&vendors=${vendors.join(
      ','
    )}&contract_categories=${contract_categories.join(',')}`,
    method: HTTP_METHODS.GET
  })

export const getAvailableOwnersRequestAsync = async (
  params?: TAvailableOwnersParams
) =>
  fetcher.request<undefined, ResponseWithPagination<TOwnerData>>({
    url: `${API_DICTIONARY.USERS}${API_DICTIONARY.AVAILABLE_OWNERS}`,
    method: HTTP_METHODS.GET,
    params
  })

export const getAvailableResponsibleUsersRequestAsync = async (id: string) =>
  fetcher.request<unknown, TResponsibleUser[]>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.RESPONSIBLE_USERS}`,
    method: HTTP_METHODS.GET
  })

export const acceptContract = async (id: string, data: {}) =>
  fetcher.request<Record<string, never>>({
    url: `${API_DICTIONARY.ANALYSIS}${API_DICTIONARY.CONTRACT_GROUP}${id}/${API_DICTIONARY.ACCEPT}`,
    method: HTTP_METHODS.PATCH,
    data
  })

export const getClinicalReviewResults = async (id: string) =>
  fetcher.request<unknown, TClinicalReview>({
    url: `${API_DICTIONARY.ANALYSIS}${API_DICTIONARY.CONTRACT_GROUP}${id}/${API_DICTIONARY.CLINICAL_REVIEW_STATISTICS}`,
    method: HTTP_METHODS.GET
  })
export const getClinicalReviewHospitalUsersResults = async (id: string) =>
  fetcher.request<
    unknown,
    Partial<ResponseWithPagination<TClinicalReviewHospitalUserItem>>
  >({
    url: `${API_DICTIONARY.ANALYSIS}${API_DICTIONARY.CONTRACT_GROUP}${id}/${API_DICTIONARY.CLINICAL_REVIEW_STAKEHOLDERS}`,
    method: HTTP_METHODS.GET
  })

export const terminateContract = async (id: string, data: {}) =>
  fetcher.request<Record<string, never>>({
    url: `${API_DICTIONARY.ANALYSIS}${API_DICTIONARY.CONTRACT_GROUP}${id}/${API_DICTIONARY.TERMINATE}`,
    method: HTTP_METHODS.PATCH,
    data
  })

export const uploadEvaluationDocuments = async (
  uuid: string,
  data: TEvaluationDocuments
) =>
  fetcher.request<TEvaluationDocuments, TEvaluationDocumentsUploadResponse>({
    url: `${API_DICTIONARY.ANALYSIS}${API_DICTIONARY.EVALUATION_PO_DOCUMENTS}${uuid}/`,
    method: HTTP_METHODS.PATCH,
    data
  })
export const getUploadedEvaluationDocuments = async () =>
  fetcher.request<unknown, TUploadedPoDocumentsResponse>({
    url: `${API_DICTIONARY.ANALYSIS}${API_DICTIONARY.EVALUATION_PO_DOCUMENTS}`,
    method: HTTP_METHODS.GET
  })

export const getUploadedEvaluationDocumentsForHealthSystem = async (
  id: string
) =>
  fetcher.request<unknown, TUploadedPoDocumentsResponse>({
    url: `${API_DICTIONARY.ANALYSIS}${API_DICTIONARY.EVALUATION_PO_DOCUMENTS}${API_DICTIONARY.HEALTH_SYSTEM}${id}/`,
    method: HTTP_METHODS.GET
  })

export const getContractCoUsers = async (id, data) =>
  fetcher.request<
    TContractUsersRequest,
    ResponseWithPagination<TContractUsersData>
  >({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.FACILITY_USERS}${API_DICTIONARY.CO_USERS}`,
    method: HTTP_METHODS.GET,
    ...data
  })
export const getContractVendorUsers = async (id, data) =>
  fetcher.request<
    TContractUsersRequest,
    ResponseWithPagination<TContractUsersData>
  >({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.FACILITY_USERS}${API_DICTIONARY.VENDOR_USERS}`,
    method: HTTP_METHODS.GET,
    ...data
  })

// TODO: check this
export const getAvailableUsersForSignatory = async (id) =>
  fetcher.request<unknown, ResponseWithPagination<TSignatoryUserOption>>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.OPTIONS}${API_DICTIONARY.SIGNATORY}`,
    method: HTTP_METHODS.GET
  })

// TODO: check this
export const addSignatoryAction = async (id, data: TAddSignatoryRequestData) =>
  fetcher.request<unknown, unknown>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.SIGNATORY}`,
    method: HTTP_METHODS.POST,
    data
  })

export const reassignSignatoryAction = async (
  id,
  signatoryId,
  data: TReassignSignatoryAction
) =>
  fetcher.request<unknown, unknown>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.SIGNATORY}${signatoryId}/${API_DICTIONARY.REASSIGNMENT}`,
    method: HTTP_METHODS.POST,
    data
  })

export const getAvailableHSUsersForSignatory = async (
  id: string,
  hsId: string
) =>
  fetcher.request<unknown, ResponseWithPagination<TSignatoryUserOption>>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.FACILITIES}${API_DICTIONARY.HEALTH_SYSTEMS}${hsId}/${API_DICTIONARY.OPTIONS}${API_DICTIONARY.COMMUNITY_SIGNATORY}`,
    method: HTTP_METHODS.GET
  })

export const getAvailableHSUsersForHospitalSignatory = async (
  id: string,
  hospitalId: string
) =>
  fetcher.request<unknown, ResponseWithPagination<TSignatoryUserOption>>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.FACILITIES}${API_DICTIONARY.HOSPITALS}${hospitalId}/${API_DICTIONARY.OPTIONS}${API_DICTIONARY.COMMUNITY_SIGNATORY}`,
    method: HTTP_METHODS.GET
  })

export const getAvailableVendorUsersForSignatory = async (
  id: string,
  hsId: string
) =>
  fetcher.request<unknown, ResponseWithPagination<TSignatoryUserOption>>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.FACILITIES}${API_DICTIONARY.HEALTH_SYSTEMS}${hsId}/${API_DICTIONARY.OPTIONS}${API_DICTIONARY.VENDOR_SIGNATORY}`,
    method: HTTP_METHODS.GET
  })

export const getAvailableVendorUsersForHospitalSignatory = async (
  id: string,
  hospitalId: string
) =>
  fetcher.request<unknown, ResponseWithPagination<TSignatoryUserOption>>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.FACILITIES}${API_DICTIONARY.HOSPITALS}${hospitalId}/${API_DICTIONARY.OPTIONS}${API_DICTIONARY.VENDOR_SIGNATORY}`,
    method: HTTP_METHODS.GET
  })

export const updateContractTerminationDateRequestAsync = async (
  id: string,
  data: { termination_date: string }
) =>
  fetcher.request<Partial<IContractDetails>, IContractDetails>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.TERMINATE}`,
    method: HTTP_METHODS.PATCH,
    data
  })

export const getVersionHistoryAsync = async (id: string, hsId: string) =>
  fetcher.request<unknown, ResponseWithPagination<TLoPDocument>>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.FACILITIES}${API_DICTIONARY.HEALTH_SYSTEMS}${hsId}/${API_DICTIONARY.DOCUMENT_HISTORY}`,
    method: HTTP_METHODS.GET
  })

export const getVersionHistoryHospitalAsync = async (
  id: string,
  hospitalId: string
) =>
  fetcher.request<unknown, ResponseWithPagination<TLoPDocument>>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.FACILITIES}${API_DICTIONARY.HOSPITALS}${hospitalId}/${API_DICTIONARY.DOCUMENT_HISTORY}`,
    method: HTTP_METHODS.GET
  })

export const getContractNoteAsync = async (id: string, noteId: string) =>
  fetcher.request<unknown, TNotesResponse>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.NOTES}${noteId}/`,
    method: HTTP_METHODS.GET
  })

export const setContractNoteAsync = async (
  id: string,
  data: TNotesUpdateRequest
) =>
  fetcher.request<unknown, TNotesResponse>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.NOTES}`,
    method: HTTP_METHODS.POST,
    data
  })

export const updateContractNoteAsync = async (
  id: string,
  noteId: string,
  data: TNotesUpdateRequest
) =>
  fetcher.request<unknown, TNotesResponse>({
    url: `${API_DICTIONARY.CONTRACTS}${id}/${API_DICTIONARY.NOTES}${noteId}/`,
    method: HTTP_METHODS.PATCH,
    data
  })
